import React from "react";
import {
  Table as AntTable,
  Space,
  Popconfirm,
  Input,
  Button,
  Modal,
  Form,
  message,
} from "antd";
import { useState, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { genNumberTable } from "../../../../function/genNumberTable";
import { useStateContext } from "../../../../contexts/ContextProvider";
import DetailsModal from "./DetailsModal";
import { UseFetchReportOutput } from "../hooks/useFetch";
import ReportModal from "./ReportModal";

const headerStyle = {
  color: "white",
  fontWeight: "bold",
  backgroundColor: "black",
};

const REPORT_BUTTON_STYLE = {
  backgroundColor: "#722ed1",
  borderColor: "#722ed1",
};

// เคสที่ Report ไปแล้ว — ม่วงจาง กดไม่ได้
const REPORTED_BUTTON_STYLE = {
  backgroundColor: "#f4ecfd",
  borderColor: "#d3adf7",
  color: "#9254de",
};

const Table = ({ data, onEdit, onDelete, loading, isHistory = false, onRejectCancel, refetch }) => {
  const [setSearchText] = useState("");
  const [setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form] = Form.useForm();
  const [modalReportVisible, setModalReportVisible] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const { mutateAsync: reportOutput } = UseFetchReportOutput();
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>

          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const onReportModalOk = async () => {
    if (reportSubmitting) return;
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return; // ฟอร์มไม่ผ่าน validate — antd โชว์ให้แล้ว
    }
    setReportSubmitting(true);
    try {
      await reportOutput({
        id: selectedRecord.id,
        reportRemark: values.reportRemark,
      });
      message.success("Report Output Success");
      setModalReportVisible(false);
      setSelectedRecord(null);
      form.resetFields();
      refetch();
    } catch (error) {
      // backend ตอบกลับว่าเคสนี้ถูก Report ไปแล้ว ให้ผู้ใช้เห็นข้อความจริง
      message.error(error?.response?.data?.message || error?.message || "Report ไม่สำเร็จ");
      refetch();
    } finally {
      setReportSubmitting(false);
    }
  };
  const columns = [
    {
      title: "ลำดับ",
      align: "center",
      //width: 60,
      dataIndex: "number",
      key: "number",
      onHeaderCell: () => {
        return { style: headerStyle };
      },
    },
    {
      title: "CaseName",
      align: "center",
      dataIndex: "caseName",
      key: "caseName",
      //width: 90,
      onHeaderCell: () => {
        return { style: headerStyle };
      },
      render: (caseName) => {
        const getCaseNameColor = (caseName) => {
          switch (caseName) {
            case 'deposit':
              return 'green';
            case 'withdraw':
              return 'red';
            case 'ETC':
              return 'purple';
            case 'transfer':
              return 'purple';
            default:
              return 'black';
          }
        };
        return <span style={{ fontSize: '16px', fontWeight: 'bold', color: getCaseNameColor(caseName) }}>{caseName.toUpperCase()}</span>;
      },
      ...getColumnSearchProps("caseName"),
    },
    {
      title: "UserId",
      align: "center",
      dataIndex: "userId",
      key: "userId",
      //width: 90,
      onHeaderCell: () => {
        return { style: headerStyle };
      },
      ...getColumnSearchProps("userId"),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "status",
      //width: 120,
      key: "status",
      onHeaderCell: () => {
        return { style: headerStyle };
      },
      render: (status) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'Success':
              return 'green'; // เหลืองส้ม
            case 'Reject':
              return 'red'; // bright red
            case 'Inform':
              return '#fa8c16'; // ส้ม — ขอข้อมูลเพิ่ม ไม่ใช่ความผิด
            case 'InformSuccess':
              return '#8c8c8c'; // เทา — ปิดแล้ว จบเรื่อง
            case 'RejectSuccess':
              return '#8c8c8c'; // เทา — ปิดแล้ว จบเรื่อง
            case 'Pending':
              return '#ffa500'; // bright orange
            case 'Waiting':
              return 'blue'; // bright blue
            default:
              return '#000000'; // black
          }
        };

        return (
          <span style={{
            color: getStatusColor(status),
            // fontWeight: 'bold',
            fontSize: '16px',
            //backgroundColor: getStatusColor(status),
          }}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getStatusColor(status),
              marginRight: '4px',
            }}></span>
            {status || "-"}
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getStatusColor(status),
              marginLeft: '4px',
            }}></span>
          </span>
        );
      },
    },
    {
      title: "วันที่อัพเดตล่าสุด",
      align: "center",
      dataIndex: "lastUpdateDate",
      key: "lastUpdateDate",
      onHeaderCell: () => ({ style: headerStyle }),
      render: (lastUpdateDate) => {
        return <span>{lastUpdateDate}</span>;
      },
    },
    {
      title: "สร้างโดย",
      align: "center",
      key: "create_by_name",
      dataIndex: "create_by_name",
      onHeaderCell: () => ({ style: headerStyle }),
      ...getColumnSearchProps("create_by_name"),
    },
    {
      // คอลัมน์เดียวใช้ได้ทั้ง 2 สถานะ — แถว Inform เก็บเหตุผลไว้คนละคอลัมน์ (inform_remark)
      // ถ้าอ่านแต่ reject_remark แถว Inform จะว่างเปล่า
      title: "เหตุผลที่ตีกลับ",
      align: "center",
      key: "return_remark",
      onHeaderCell: () => ({ style: headerStyle }),
      render: (_, record) => {
        // ตารางนี้ใช้ร่วมกับ History ที่โชว์ทุกสถานะ — เคสที่ไม่เคยโดนตีกลับต้องไม่ขึ้นป้ายเลย
        // และเคสที่แก้แล้ว (กลับเป็น Waiting) ยังเก็บเหตุผลเดิมไว้ ควรเห็นย้อนหลังได้
        // เคสที่เคยโดนทั้ง 2 แบบ ก็โชว์ทั้งคู่ ไม่ต้องเดาว่าอันไหนใหม่กว่า
        const blocks = [
          record.inform_remark && { key: 'inform', label: 'ขอข้อมูลเพิ่ม', color: '#fa8c16', text: record.inform_remark },
          record.reject_remark && { key: 'reject', label: 'Reject', color: '#f44336', text: record.reject_remark },
        ].filter(Boolean);

        if (blocks.length === 0) return <span>-</span>;

        return (
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left' }}>
            {blocks.map((b) => (
              <div key={b.key} style={{ marginBottom: 4 }}>
                <span
                  style={{
                    display: 'inline-block',
                    marginBottom: 2,
                    padding: '0 6px',
                    borderRadius: 4,
                    fontSize: 12,
                    color: '#fff',
                    background: b.color,
                  }}
                >
                  {b.label}
                </span>
                <div>{b.text}</div>
              </div>
            ))}
          </div>
        );
      },
      width: 200,
    },
    {
      title: "Note.",
      align: "center",
      onHeaderCell: () => ({ style: headerStyle }),
      render: (record) => {
        // ตรวจสอบว่า customFields เป็น array ก่อนใช้ find เพื่อป้องกัน Uncaught TypeError
        let note = null;
        const customFields = record?.dataManage?.customFields;
        if (Array.isArray(customFields)) {
          note = customFields.find((field) => field.title === "NOTE.")?.value ?? null;
        }
        return <span>{note}</span>;
      },
      width: 150,
    },
    {
      title: "รูปภาพ",
      align: "center",
      key: "images",
      //width: 50,
      onHeaderCell: () => ({ style: headerStyle }),
      render: (record) => {
        if (!record.imgRecipe || !Array.isArray(record.imgRecipe) || record.imgRecipe.length === 0 || record.imgRecipe[0].img_url === null) {
          return "-";
        }

        return (
          <Button
            type="primary"
            onClick={() => {
              Modal.info({
                title: "รูปภาพ",
                width: 800,
                content: (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginTop: "20px",
                    }}
                  >
                    {record.imgRecipe.map((image, index) => (
                      <div key={index} style={{ width: "200px" }}>
                        <img
                          src={image.img_url}
                          alt={`Image ${index + 1}`}
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "4px",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ),
                okText: "ปิด",
              });
            }}
          >
            ดู
          </Button>

        );
      }
    },
    {
      title: "รายละเอียด",
      // fixed: "right",
      key: "details",
      align: "center",
      //width: 50,
      render: (record) => {
        // เดิมไม่มี Inform/InformSuccess ในลิสต์ → เคส Inform ไม่มีปุ่มรายละเอียดให้กดเลย
        // เป็นอีกสาเหตุที่รูปหลักฐานที่ panel แนบมา "ไม่แสดง"
        if (record.status == 'Success' || record.status == 'Reject' || record.status == 'Pending'
          || record.status == 'RejectSuccess' || record.status == 'Inform' || record.status == 'InformSuccess') {
          return (
            <Button
              type="default"
              width={50}
              onClick={() => {
                setSelectedRecord(record);
                setDetailsModalVisible(true);
              }}
            >
              ดู
            </Button>
          );
        }
        return null;
      },
      onHeaderCell: () => ({ style: headerStyle }),
    },
    ...(isHistory
      ? []
      : [
        {
          title: "การจัดการ",
          // fixed: "right",
          key: "actions",
          align: "center",
          //width: 100,
          render: (record) => (
            <Space size="middle">
              <Button
                type="primary"
                onClick={() => onEdit(record)}
              >
                {record.status === 'Inform' ? 'เติมข้อมูล' : 'แก้ไข'}
              </Button>
              {/* Inform ก็ปิดได้ สำหรับเคสที่เคลียร์เรียบร้อยแล้ว (ได้ข้อมูลมาทางอื่น/ไม่ต้องใช้แล้ว)
                  ปลายทางแยกกัน: Reject → RejectSuccess · Inform → InformSuccess */}
              <Button
                type="primary"
                onClick={() => onRejectCancel(record)}
              >
                {record.status === 'Inform' ? 'Cancel Inform' : 'Cancel Reject'}
              </Button>
            </Space>
          ),
          onHeaderCell: () => ({ style: headerStyle }),
        },
      ]),
    ...(isHistory ? [
      {
        title: "การจัดการ",
        // fixed: "right",
        key: "actions",
        align: "center",
        //width: 100,
        render: (record) => {
          if (record.status !== 'Success') return null;
          // Report ได้ครั้งเดียวต่อเคส — เคยส่งแล้วยังโชว์ปุ่มไว้แต่กดไม่ได้
          if (record.isReport) {
            return (
              <Space size="middle">
                <Button disabled style={REPORTED_BUTTON_STYLE}>
                  Report แล้ว
                </Button>
              </Space>
            );
          }
          return (
            <Space size="middle">
              <Button
                type="primary"
                style={REPORT_BUTTON_STYLE}
                onClick={() => {
                  setModalReportVisible(true);
                  setSelectedRecord(record);
                }}
              >
                Report
              </Button>
            </Space>
          );
        },
        onHeaderCell: () => ({ style: headerStyle }),
      },
    ] : [])
  ];


  return (
    <div className="overflow-x-auto" style={{ minWidth: 0, maxWidth: '100%' }}>
      <AntTable
        loading={loading}
        className="border-1 rounded-lg border-gray-200"
        dataSource={genNumberTable(data)}
        columns={columns}
        scroll={{ x: 'max-content' }}
        bordered
        pagination={true}
        pageSize={10}
        pageSizeOptions={[10, 20, 50, 100]}
        showSizeChanger={true}
        showQuickJumper={true}
      />
      <DetailsModal
        visible={detailsModalVisible}
        onClose={() => {
          setDetailsModalVisible(false);
          setSelectedRecord(null);
        }}
        record={selectedRecord}
      />

      <ReportModal
        form={form}
        open={modalReportVisible}
        onOk={onReportModalOk}
        confirmLoading={reportSubmitting}
        onCancel={() => {
          setModalReportVisible(false)
          setSelectedRecord(null);
          form.resetFields();
        }}
      >
      </ReportModal>
    </div>

  );
};

export default Table;
